/* =========================================================================
 * Kkumi · Wishlist Core
 * -------------------------------------------------------------------------
 * Drop-in replacement for the Swish / Wishlist King client. Provides the same
 * `window.WishlistKing` surface the existing wishlist scripts consume, but
 * backed by our own Cloudflare Worker (via the Shopify App Proxy at
 * /apps/wishlist). Must load BEFORE kkumi-wishlist*.js.
 *
 * Surface reproduced (see kkumi-wishlist.js / -grid.js / -recommendations.js):
 *   WK.config.routes.apiHost                  -> proxy prefix (/apps/wishlist)
 *   WK.config.storefront.accessToken/version  -> Storefront API (product resolve)
 *   WK._apis.wishlist.headers                 -> {} (auth is the signed proxy)
 *   WK.addWishlistItem(payload)               -> POST  /wishlists/mine/items
 *     (payload.properties: optional cart line properties — a configured
 *      bundle's choices — saved with the item and returned on every read.
 *      Ad event only, never sent to the Worker: payload.title / price / currency
 *      when the page already has them, and payload.restore:true for an Undo that
 *      puts back something just removed, which is not a new save for the ad pixel.)
 *   WK.removeWishlistItem(payload)            -> DELETE /wishlists/mine/items/:id
 *     (optimistic: the local copy changes first and every failure is swallowed.
 *      Pass `{ confirm: true }` as a second argument to WAIT for the Worker
 *      instead: it resolves once the item is gone and rejects otherwise, with
 *      the local copy untouched — the bought card's Remove button uses that)
 *   WK.removeWithUndo(payload) -> { undo(), commit(), done }
 *     (the account tab's Undo toast: gone from the local copy at once, the
 *      DELETE only on commit() — toast gone, page left, or 30s. undo() before
 *      that keeps the very same row; see "a removal that can be undone")
 *   document 'wk:countUpdated' event          -> emitted on every change
 *
 * Extras over the old client:
 *   - localStorage read-through cache: GET /wishlists/mine is served from cache
 *     within a session (stale-while-revalidate), so a browse session is ~1 call.
 *   - Members only (owner, 28 Oct 2026): saving needs a signed-in account. A
 *     guest's list reads as empty and a save is refused (login_required).
 *     Saves a guest made on a device before then still merge into the account
 *     once, on sign-in (POST /wishlists/mine/merge).
 *   - Club members only (owner, 28 Oct 2026; the Wishlist app's Settings → Club
 *     members): when the app says so, only Kkumi Club members can save. The app
 *     enforces it and tells us in `club` on GET /wishlists/mine: `{ required,
 *     member }`. A signed-in shopper who hasn't joined reads an empty list,
 *     `addWishlistItem` rejects with 'club_required' (no request when we already
 *     know; the app's 403 club_required when we didn't), and `WK.club.prompt(el)`
 *     shows the one shared "Join the Kkumi Club" prompt. `required: false` (the
 *     default) changes nothing for anyone. `WK.club.reveal(root)` unhides the
 *     [data-kk-wl-club-only] adverts ("wishlist is for Club members") only while
 *     the app says it is. Fires `wk:clubUpdated` when what we know changes.
 *   - Fires client-side Klaviyo "Added/Removed from Wishlist" events when the
 *     onsite snippet is present.
 *   - Publishes the Shopify customer event `kkumi:wishlist_added` once per
 *     genuinely new save the Worker clears for ads (`ads: true`: not a shopper
 *     known to be under 18; see "Shopify customer event" below), for the ad
 *     custom pixel in apps/oracle-pixel/custom-pixels.
 * ========================================================================= */
(function () {
  'use strict';
  if (window.WishlistKing) return; // never clobber an already-present client

  var cfg = window.__kkWishCfg || {};
  var API = cfg.proxy || '/apps/wishlist';
  var MINE = API + '/wishlists/mine';
  var loggedIn = !!cfg.loggedIn;
  var TTL = 5 * 60 * 1000; // cache freshness window
  var GUEST_KEY = 'kkwl:guest:v1';
  // Bumped v1 -> v2 to wipe pre-fix caches that could hold a "zombie" item
  // (saved row whose product no longer resolves on the storefront, so the
  // grid filtered it out while the bubble kept showing the stale count).
  // The old key is cleaned up below so it doesn't sit in localStorage forever.
  var CACHE_KEY = 'kkwl:cache:' + (cfg.customerId || 'anon') + ':v2';
  var LEGACY_CACHE_KEY = 'kkwl:cache:' + (cfg.customerId || 'anon') + ':v1';
  var MERGED_KEY = 'kkwl:merged:' + (cfg.customerId || '');
  var CLUB_ANON_KEY = 'kkwl:club:anon:v1'; // a guest's copy of "is the wishlist Club-only?" (a signed-in shopper's rides in their wishlist cache)
  var CLUB_ANON_TTL = 10 * 60 * 1000;
  var CLUB_RECHECK_MS = 10 * 1000; // a shopper known NOT to be a member is re-checked on a page this often (they may just have joined)

  // Bind the native fetch up front; all our own network calls use it directly
  // so they are never caught by the read-through interceptor below.
  var net = window.fetch ? window.fetch.bind(window) : null;

  /* ---------------- storage ---------------- */
  function readStore(key) { try { return JSON.parse(localStorage.getItem(key)); } catch (e) { return null; } }
  function writeStore(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) {} }
  function delStore(key) { try { localStorage.removeItem(key); } catch (e) {} }

  function emptyWl() { return { id: '', publicId: '', ownerName: cfg.ownerName || null, title: null, items: [] }; }
  function cacheGet() { return readStore(CACHE_KEY); }            // { wl, ts }
  function cacheSet(wl) { writeStore(CACHE_KEY, { wl: wl, ts: Date.now(), club: clubNow }); }
  // One-shot: drop the pre-v2 cache so a stuck "zombie" count from an older
  // session is wiped on first load after this ships. Safe even when absent.
  delStore(LEGACY_CACHE_KEY);
  function itemsEqual(a, b) { try { return JSON.stringify(a) === JSON.stringify(b); } catch (e) { return false; } }

  /* ---------------- Club members only ----------------
   * `club` on GET /wishlists/mine: { required: false } (the app's setting is off: nothing changes for anyone) or
   * { required: true, member: bool } (on: only a shopper who has joined the Kkumi Club may save). A guest has not
   * joined. When the app couldn't ask Kkumi Rewards it fails open (member: true), so we do too. */
  var clubNow = null; // { required, member } as last told by the app, or null = not known yet
  var CLUB = cfg.club || {}; // the shared prompt's copy + join link (theme settings → Wishlist → Club-only prompt)
  function normClub(c) {
    if (!c || typeof c !== 'object') return null;
    return c.required === true ? { required: true, member: c.member !== false } : { required: false, member: true };
  }
  function readClub() {
    if (loggedIn) { var c = cacheGet(); return c ? normClub(c.club) : null; }
    var g = readStore(CLUB_ANON_KEY);
    return g && (Date.now() - g.ts) < CLUB_ANON_TTL ? normClub(g.club) : null;
  }
  function clubBlocked() { return !!(loggedIn && clubNow && clubNow.required && !clubNow.member); }
  function clubError() { var e = new Error('club_required'); e.code = 'club_required'; return e; }
  function fireClub(next) {
    var ev, detail = { club: next };
    try { ev = new CustomEvent('wk:clubUpdated', { detail: detail, bubbles: true }); }
    catch (e) { ev = document.createEvent('CustomEvent'); ev.initCustomEvent('wk:clubUpdated', true, true, detail); }
    document.dispatchEvent(ev);
  }
  function setClub(c) {
    var next = normClub(c);
    if (!next) return;
    var changed = !clubNow || clubNow.required !== next.required || clubNow.member !== next.member;
    clubNow = next;
    if (loggedIn) { var e = cacheGet(); if (e) { e.club = next; writeStore(CACHE_KEY, e); } }
    else writeStore(CLUB_ANON_KEY, { club: next, ts: Date.now() });
    if (changed) { fireClub(next); if (!clubBlocked()) maybeMerge(); }
  }
  // Resolve what we know about the app's Club-only setting (a guest's page only asks when a page has an advert to show).
  var clubWaiters = [];
  var clubAsking = false;
  function ensureClub(cb) {
    if (clubNow) { cb(clubNow); return; }
    clubWaiters.push(cb);
    if (clubAsking || !net) return;
    clubAsking = true;
    (mineInflight || trackMine(netGetMine())).then(function (wl) { if (loggedIn) adoptMine(wl); }).catch(function () {}).then(function () {
      clubAsking = false;
      var c = clubNow || { required: false, member: true }; // no answer: as if off, so nothing is promised that isn't enforced
      clubWaiters.splice(0).forEach(function (f) { try { f(c); } catch (e) {} });
    });
  }
  // [data-kk-wl-club-only] = an advert for "the wishlist is for Club members". It ships hidden and shows only while the
  // app's setting is on, so the claim is never made while anyone signed in can still save. The theme editor shows them all.
  function reveal(root) {
    if (window.Shopify && window.Shopify.designMode) return;
    var nodes = (root || document).querySelectorAll('[data-kk-wl-club-only]');
    if (!nodes.length) return;
    ensureClub(function (c) { Array.prototype.forEach.call(nodes, function (n) { n.hidden = !c.required; }); });
  }
  // A signed-in shopper's answer rides on their list cache, which a page with no hearts (the Club page) never refreshes:
  // on a page with an advert, an answer older than the cache's freshness window is re-read in the background (the one
  // shared revalidation, so a page that reads the list anyway still makes a single request) and the advert follows it.
  function refreshStaleClub() {
    if (!loggedIn || !net || !document.querySelector('[data-kk-wl-club-only]')) return;
    var c = cacheGet();
    if (c && (Date.now() - c.ts) > TTL) revalidate();
  }
  function safeHref(u) {
    u = String(u == null ? '' : u);
    return (/^https?:\/\//i.test(u) || /^\/(?!\/)/.test(u) || /^#[A-Za-z][\w-]*$/.test(u)) ? u : '/pages/kkumi-club';
  }
  function joinHref() { return safeHref(CLUB.joinUrl || '/pages/kkumi-club#kkumi-club-join'); }
  // A host's own prompt (the product page's popover: data-kk-join-pop="<id>" on the heart) opens and closes in place.
  var closePopover = null; // closes the join popover that is open, if any (one at a time, one set of listeners)
  function togglePopover(pop, anchor) {
    var wasOpen = !pop.hidden;
    if (closePopover) closePopover(); // pressing the heart again closes it; another one opening closes this one
    if (wasOpen) return;
    var onDoc = function (e) { if (!pop.contains(e.target) && !(anchor && anchor.contains(e.target))) closePopover(); };
    var onKey = function (e) { if (e.key === 'Escape') { closePopover(); if (anchor) anchor.focus(); } };
    closePopover = function () {
      closePopover = null;
      pop.hidden = true;
      if (anchor) anchor.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDoc, true);
      document.removeEventListener('keydown', onKey, true);
    };
    pop.hidden = false;
    if (anchor) anchor.setAttribute('aria-expanded', 'true');
    // After this press has finished, so the click that opened it can't close it (and only if it is still the open one).
    var mine = closePopover;
    setTimeout(function () { if (closePopover === mine) { document.addEventListener('click', onDoc, true); document.addEventListener('keydown', onKey, true); } }, 0);
    var link = pop.querySelector('a');
    if (link) link.focus();
  }
  // The shared prompt: the sitewide top-right popup (<cart-toast>) with the join link. Never saves anything.
  function promptJoin(anchor) {
    var popId = anchor && anchor.getAttribute && anchor.getAttribute('data-kk-join-pop');
    var pop = popId ? document.getElementById(popId) : null;
    if (pop) { togglePopover(pop, anchor); return; }
    var ct = document.querySelector('cart-toast');
    if (ct && typeof ct.renderMessage === 'function') {
      ct.renderMessage({
        heading: CLUB.heading || 'Wishlists are for Club members',
        body: CLUB.text || 'Join the Kkumi Club to save this to your wishlist. It’s free.',
        action: { href: joinHref(), label: CLUB.cta || 'Join free →' }
      });
      ct.setAttribute('role', 'status');
      if (typeof ct.show === 'function') ct.show({ duration: 9000 }); // a prompt with a link needs longer than the cart-add 5s
      return;
    }
    window.location.assign(joinHref()); // no popup on this page: the join page is where the heart was heading
  }

  /* ---------------- events ---------------- */
  function countOf(wl) { return (wl && wl.items) ? wl.items.length : 0; }
  function currentCount() {
    if (!loggedIn) return 0;
    var c = cacheGet();
    return c && c.wl ? countOf(c.wl) : 0;
  }
  // The header counter (section-kkumi-navbar-clean.js) reads e.detail.count.
  // Mark our events with detail.source so the count consumers can ignore
  // copies dispatched by the embedded swish-wishlist-king @app block — it
  // keeps its own internal count that doesn't know about our worker's
  // writes, and re-fires `wk:countUpdated` from that stale state on every
  // heart click, which would flash the bubble (e.g. 0 → 1 → 0 when the user
  // removes their last item).
  function emitCount() {
    var detail = { count: currentCount(), source: 'kkwl' };
    var ev;
    try { ev = new CustomEvent('wk:countUpdated', { detail: detail, bubbles: true }); }
    catch (e) { ev = document.createEvent('CustomEvent'); ev.initCustomEvent('wk:countUpdated', true, true, detail); }
    document.dispatchEvent(ev);
  }
  function jsonResponse(obj) {
    return new Response(JSON.stringify(obj), { status: 200, headers: { 'Content-Type': 'application/json' } });
  }

  /* ---------------- storefront token (served by the Worker) ----------------
   * The backend mints + returns the public Storefront API token alongside
   * wishlist reads, so the theme needs no manual token setting. A value in
   * __kkWishCfg (theme setting) still wins as an override. */
  var SFTOK_KEY = 'kkwl:sftok:v1';
  function adoptStorefront(sf) {
    if (!sf || !sf.token) return;
    if (!cfg.storefrontToken) {
      WK.config.storefront.accessToken = sf.token;
      if (sf.version) WK.config.storefront.version = sf.version;
    }
    writeStore(SFTOK_KEY, { token: sf.token, version: sf.version || null });
  }

  /* ---------------- network ---------------- */
  function netGetMine() {
    return net(MINE, { headers: {}, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        adoptStorefront(d && d.storefront);
        if (d && d.club) setClub(d.club);
        return (d && d.wishlist) ? d.wishlist : emptyWl();
      });
  }

  // The read of GET /wishlists/mine now in flight, if any: an advert's Club-only check rides on it instead of asking again.
  var mineInflight = null;
  function trackMine(p) {
    mineInflight = p;
    var done = function () { if (mineInflight === p) mineInflight = null; };
    p.then(done, done);
    return p;
  }

  var revalidating = false;
  // A fresh copy of the list from the app: keep it, and tell the page when it changed (the grid redraws on
  // wk:countUpdated). Every background read goes through here, so whichever one lands first announces the change:
  // a read that only cached it would leave the next one comparing against the new copy and finding nothing new.
  function adoptMine(wl) {
    var prev = cacheGet();
    cacheSet(wl);
    if (!prev || !itemsEqual(prev.wl.items, wl.items)) emitCount();
  }
  function revalidate() {
    if (revalidating) return;
    revalidating = true;
    netGetMine().then(function (wl) {
      revalidating = false;
      adoptMine(wl);
    }).catch(function () { revalidating = false; });
  }

  // Resolve GET /wishlists/mine: a guest gets an empty list, a member the cache
  // (revalidating in the background when stale), cold-fetching otherwise.
  function serveMine() {
    if (!loggedIn) return Promise.resolve(jsonResponse({ wishlist: emptyWl(), club: clubNow }));
    var c = cacheGet();
    // A shopper known NOT to be a Club member is asked about again once what we know is a few seconds old: they may
    // just have joined. What we have is served at once, and the fresh answer follows (wk:clubUpdated).
    var recheck = !!(c && clubBlocked() && (Date.now() - c.ts) > CLUB_RECHECK_MS);
    if (c && !recheck && (Date.now() - c.ts) < TTL) return Promise.resolve(jsonResponse({ wishlist: withProps(c.wl), club: clubNow }));
    if (c) { revalidate(); return Promise.resolve(jsonResponse({ wishlist: withProps(c.wl), club: clubNow })); }
    return trackMine(netGetMine())
      .then(function (wl) { cacheSet(wl); emitCount(); return jsonResponse({ wishlist: withProps(wl), club: clubNow }); })
      .catch(function () { return jsonResponse({ wishlist: emptyWl(), club: clubNow }); });
  }

  /* ---------------- read-through interceptor ----------------
   * Transparently serve the member's own list from cache (a guest's is empty) so the three
   * consumer scripts stay unchanged. Only the exact `GET {API}/wishlists/mine`
   * is diverted; everything else (incl. shared lists and our own writes) falls
   * straight through to the native fetch. Any hiccup => native fetch. */
  if (net) {
    // Pass a request through to the network, tapping its response for the
    // Worker-served storefront config (response object is returned untouched).
    var passTapping = function (input, init, isMine) {
      return net(input, init).then(function (res) {
        try { res.clone().json().then(function (d) { adoptStorefront(d && d.storefront); if (isMine && d && d.club) setClub(d.club); }).catch(function () {}); } catch (e) {}
        return res;
      });
    };
    window.fetch = function (input, init) {
      try {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var method = ((init && init.method) || (typeof input !== 'string' && input && input.method) || 'GET').toUpperCase();
        if (method === 'GET' && url.indexOf(API) !== -1 && /\/wishlists\/mine(?:[?#]|$)/.test(url)) {
          // Explicit cache-busting reads (the heart's server-verify after an
          // add/remove) must hit the network for fresh server truth — never the
          // local cache, or the verify races our in-flight write and false-fails.
          var bust = (init && (init.cache === 'no-store' || init.cache === 'reload')) || /[?&]_t=/.test(url);
          return bust ? passTapping(input, init, true) : serveMine();
        }
        // Shared-list reads pass through, tapped for the storefront config.
        if (method === 'GET' && url.indexOf(API + '/wishlists/') !== -1) {
          return passTapping(input, init);
        }
      } catch (e) {}
      return net(input, init);
    };
  }

  /* ---------------- saved cart-line properties ----------------
   * A configured bundle (Simple Bundles Infinite Options) is its parent variant
   * PLUS cart line properties — kit, Galaxy colour, bag colour and
   * `_bundle_selection`. Saved without them, "Move to wishlist" and back added
   * the bare parent: checkout showed the $35.95 parent with no components.
   * Items now carry `properties` to the Worker (stored per item, migration 0006)
   * and back. The Worker enforces the same bounds (normaliseProperties). */
  function cleanProps(p) {
    if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
    var out = {}, n = 0;
    Object.keys(p).forEach(function (k) {
      var v = p[k];
      if (k && k.length <= 120 && typeof v === 'string' && v.length <= 500 && n < 20) { out[k] = v; n++; }
    });
    return n ? out : null;
  }
  // Same-browser copy, so a list read from a Worker that doesn't store
  // properties yet (before its 0006 deploy), or a cache refreshed from one,
  // still hands the choices back. Server values win when present.
  var PROPS_KEY = 'kkwl:props:' + (cfg.customerId || 'anon') + ':v1';
  function rememberProps(productId, variantId, props) {
    var m = readStore(PROPS_KEY) || {};
    var k = vkey(productId, variantId);
    if (props) m[k] = props;
    else if (m[k]) delete m[k];
    else return;
    writeStore(PROPS_KEY, m);
  }
  function withProps(wl) {
    var m = readStore(PROPS_KEY);
    if (!wl || !wl.items || !m || !Object.keys(m).length) return wl;
    var out = {};
    Object.keys(wl).forEach(function (k) { out[k] = wl[k]; });
    out.items = wl.items.map(function (i) {
      if (i.properties) return i;
      var p = m[vkey(i.productId, i.variantId)];
      if (!p) return i;
      var copy = {};
      Object.keys(i).forEach(function (k) { copy[k] = i[k]; });
      copy.properties = p;
      return copy;
    });
    return out;
  }

  /* ---------------- local mutations ---------------- */
  // "productId|variantId" identity so distinct variants are independent saves
  // ('' variant = no specific variant).
  function vkey(productId, variantId) {
    var vid = (variantId == null || variantId === '') ? '' : String(variantId);
    if (!/^\d+$/.test(vid)) vid = '';
    return String(productId) + '|' + vid;
  }
  function localRemove(wl, productId, variantId) {
    var k = vkey(productId, variantId);
    wl.items = wl.items.filter(function (i) { return vkey(i.productId, i.variantId) !== k; });
    return wl;
  }

  /* ---------------- Klaviyo (best-effort) ---------------- */
  // Attach events to the logged-in customer's profile. The onsite snippet often
  // auto-identifies Shopify customers, but we do it explicitly to be sure.
  var identified = false;
  function identifyKlaviyo() {
    if (identified || !loggedIn || !cfg.email) return;
    try {
      if (window.klaviyo && window.klaviyo.identify) { window.klaviyo.identify({ email: cfg.email }); identified = true; return; }
      if (window._learnq && window._learnq.push) { window._learnq.push(['identify', { $email: cfg.email }]); identified = true; }
    } catch (e) {}
  }
  var warnedMissing = false;
  function warnIfKlaviyoMissing() {
    if (warnedMissing || !loggedIn) return;
    if (!window.klaviyo && !window._learnq) {
      warnedMissing = true;
      try { console.warn('[kkumi] Klaviyo onsite snippet missing — wishlist events will not reach Klaviyo. Confirm the klaviyo-onsite-embed app block is enabled in the live theme.'); } catch (e) {}
    }
  }
  function fireKlaviyo(metric, payload) {
    try {
      identifyKlaviyo();
      warnIfKlaviyoMissing();
      var q = window.klaviyo || window._learnq;
      if (!q || !q.push) return;
      var handle = payload && payload.productHandle;
      q.push(['track', metric, {
        ProductID: payload && payload.productId,
        Handle: handle || undefined,
        VariantID: (payload && payload.variantId) || undefined,
        URL: handle ? (location.origin + '/products/' + handle) : undefined
      }]);
    } catch (e) {}
  }

  /* ---------------- Shopify customer event (ad platforms) ----------------
   * A genuinely new save (the Worker answered added:true) is published ONCE as the
   * custom customer event `kkumi:wishlist_added`, and only when the Worker also
   * answered ads:true (Kkumi Rewards doesn't know the shopper to be under 18; the
   * privacy policy's Children's Data rule. No answer, or an older Worker: nothing). The theme sends nothing to Meta
   * or TikTok itself: the custom pixel in apps/oracle-pixel/custom-pixels
   * subscribes to this event, and Shopify runs that pixel only for visitors whose
   * cookie choices allow marketing. The data is plain ids and an event id (the
   * saved row's id, for de-duplication): never a name, email or customer id.
   * title / price / currency ride along only when the caller already had them
   * (price in major units, e.g. 12.5, with its 3-letter currency). Cookie choices:
   * when Shopify's consent API is on the page and the shopper has not allowed
   * marketing, or has opted out of data sale, nothing is published, so a save made
   * before they tapped Accept can't be replayed to the pixel afterwards. Where no
   * choice is asked (the API is absent, or allows everything) it publishes, and the
   * pixel checks the same state again. Never throws: a missing or broken
   * Shopify.analytics must not touch the save. */
  var AD_EVENT = 'kkumi:wishlist_added';
  function adAllowed() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return true;
    try {
      if (typeof cp.marketingAllowed === 'function' && !cp.marketingAllowed()) return false;
      if (typeof cp.saleOfDataAllowed === 'function' && !cp.saleOfDataAllowed()) return false;
    } catch (e) {}
    return true;
  }
  function adId(v) { v = (v == null) ? '' : String(v); return /^[1-9]\d{0,19}$/.test(v) ? v : ''; }
  function adEventData(payload, item) {
    var data = { productId: adId(item.productId != null ? item.productId : payload.productId) };
    if (!data.productId) return null;
    var vid = adId(item.variantId);
    if (vid) data.variantId = vid;
    if (item.id) data.eventId = String(item.id).slice(0, 64);
    var title = typeof payload.title === 'string' ? payload.title.trim().slice(0, 200) : '';
    if (title) data.title = title;
    var cur = typeof payload.currency === 'string' ? payload.currency.toUpperCase() : '';
    if (typeof payload.price === 'number' && isFinite(payload.price) && payload.price >= 0 && payload.price < 1e7 && /^[A-Z]{3}$/.test(cur)) {
      data.price = Math.round(payload.price * 100) / 100;
      data.currency = cur;
    }
    return data;
  }
  function publishAdded(payload, item) {
    try {
      if (!adAllowed()) return;
      var data = adEventData(payload, item);
      var an = window.Shopify && window.Shopify.analytics;
      if (!data || !an || typeof an.publish !== 'function') return;
      var out = an.publish(AD_EVENT, data);
      if (out && typeof out.then === 'function') out.then(null, function () {});
    } catch (e) {}
  }

  /* ---------------- public API ---------------- */
  function addWishlistItem(payload) {
    payload = payload || {};
    var productId = payload.productId;
    var variantId = payload.variantId || null;
    var props = cleanProps(payload.properties);
    if (productId == null) return Promise.resolve();
    // Members only: a guest's save is refused, never kept on the device.
    if (!loggedIn) return Promise.reject(new Error('login_required'));
    // Club members only: a shopper we already know hasn't joined isn't sent to the app (and no Klaviyo event is fired).
    if (clubBlocked()) return Promise.reject(clubError());
    // Its removal is still waiting on an Undo (removeWithUndo): call that off, the row was never deleted.
    var hk = vkey(productId, variantId);
    if (held[hk]) held[hk].undo();
    fireKlaviyo('Added to Wishlist', payload);

    if (!net) return Promise.resolve();
    var body = { productId: String(productId), variantId: variantId != null ? String(variantId) : null };
    if (props) body.properties = props;
    return net(API + '/wishlists/mine/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) {
      // A non-2xx (401 login_required, 400 invalid_product, 500 server_error) is
      // a genuine failure — reject so callers can react (e.g. keep the cart line
      // on a failed "move to wishlist") instead of silently caching it as saved.
      if (!r.ok) {
        return r.json().catch(function () { return null; }).then(function (e) {
          // The app's own refusal: not a Club member. Remember it (the heart shows the join prompt, not an error).
          if (r.status === 403 && e && e.error === 'club_required') { setClub({ required: true, member: false }); throw clubError(); }
          throw new Error((e && e.error) || 'wishlist_save_failed');
        });
      }
      return r.json();
    }).then(function (res) {
      // The server returns { item } only when the row actually persisted. A full
      // list comes back 200 with { added:false, capped:true } and no item —
      // that's a failure to save, not a success, so surface it rather than
      // faking a local add.
      if (!res || !res.item) throw new Error((res && res.error) || 'wishlist_save_failed');
      if (props) rememberProps(productId, variantId, props);
      var c = cacheGet(); var wl = (c && c.wl) ? c.wl : emptyWl();
      var k = vkey(productId, variantId);
      var existing = wl.items.filter(function (i) { return vkey(i.productId, i.variantId) === k; })[0];
      var saved = res.item.properties || props;
      if (existing) {
        existing.id = res.item.id; existing.createdAt = res.item.createdAt;
        if (saved) existing.properties = saved;
      } else {
        if (saved && !res.item.properties) res.item.properties = saved;
        wl.items.push(res.item);
      }
      cacheSet(wl); emitCount();
      // Only a row the Worker actually inserted counts (added === true): not an
      // item that was already saved, not a full list, not a failed save (those
      // threw above), and not an Undo putting back something the shopper just
      // removed (payload.restore), which is not a new save for the ad platforms.
      // And only when the Worker says it may go to them (ads === true): never for
      // a shopper Kkumi Rewards knows to be under 18, or when Rewards couldn't say.
      if (res.added === true && res.ads === true && !payload.restore) publishAdded(payload, res.item);
      return res;
    });
  }

  // Switch one saved item's back-in-stock alert (PATCH /wishlists/mine/items/:id).
  // Resolves { alert } on success, rejects otherwise; the cache follows.
  function setItemAlert(itemId, on) {
    if (!loggedIn || !net || !itemId || String(itemId).indexOf('local-') === 0) return Promise.reject(new Error('unavailable'));
    return net(API + '/wishlists/mine/items/' + encodeURIComponent(itemId), {
      method: 'PATCH', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ alert: !!on })
    }).then(function (r) {
      if (!r.ok) throw new Error('alert_' + r.status);
      var c = cacheGet();
      if (c && c.wl) { c.wl.items.forEach(function (i) { if (i.id === itemId) i.alert = !!on; }); cacheSet(c.wl); }
      return { alert: !!on };
    });
  }

  // The waiting form of removal, for a button that must know it worked. The ordinary form
  // below is optimistic — it drops the item from the local copy FIRST and swallows every
  // failure, so its promise always resolves. This one changes nothing local until the Worker
  // has answered: it resolves once the item is gone (a 404 means it already was) and rejects
  // on anything else (a refusal, a rate limit, a network error), so the caller can put its
  // card back as it was. Same steps as the ordinary form, in the other order.
  function removeConfirmed(payload) {
    var productId = payload.productId;
    var variantId = payload.variantId;
    if (!net) return Promise.reject(new Error('unavailable'));
    var c = cacheGet(); var wl = (c && c.wl) ? c.wl : emptyWl();
    var rkey = productId != null ? vkey(productId, variantId) : null;
    var cached = rkey != null ? wl.items.filter(function (i) { return vkey(i.productId, i.variantId) === rkey; })[0] : null;
    // The real server id from the local copy wins over the passed one, as in the ordinary form.
    var id = (cached && cached.id && String(cached.id).indexOf('local-') !== 0) ? cached.id : payload.wishlistItemId;
    if (!id || id === true || id === 'true' || String(id).indexOf('local-') === 0) return Promise.reject(new Error('unavailable'));
    return net(API + '/wishlists/mine/items/' + encodeURIComponent(id), { method: 'DELETE', credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok && r.status !== 404) throw new Error('remove_' + r.status);
        fireKlaviyo('Removed from Wishlist', payload);
        if (productId != null) {
          rememberProps(productId, variantId, null);
          var now = cacheGet(); var after = (now && now.wl) ? now.wl : emptyWl();
          localRemove(after, productId, variantId);
          cacheSet(after); emitCount();
        }
        return { removed: r.ok };
      });
  }

  function removeWishlistItem(payload, opts) {
    payload = payload || {};
    var productId = payload.productId;
    var passedId = payload.wishlistItemId;
    if (productId == null && passedId == null) return Promise.resolve();
    if (!loggedIn) return Promise.resolve(); // a guest has nothing saved
    if (opts && opts.confirm) return removeConfirmed(payload);

    var variantId = payload.variantId;
    var rkey = productId != null ? vkey(productId, variantId) : null;
    // Already on its way out with an Undo offered (removeWithUndo): send it now.
    if (rkey != null && held[rkey]) return held[rkey].commit();
    fireKlaviyo('Removed from Wishlist', payload);
    if (productId != null) rememberProps(productId, variantId, null);
    var c = cacheGet(); var wl = (c && c.wl) ? c.wl : emptyWl();
    var cached = rkey != null ? wl.items.filter(function (i) { return vkey(i.productId, i.variantId) === rkey; })[0] : null;
    if (productId != null) localRemove(wl, productId, variantId);
    cacheSet(wl); emitCount();
    return sendRemoval(cached, passedId, rkey);
  }

  // The Worker half of a removal. The real server id from the local copy wins
  // over the passed one (that may be a stale optimistic value, e.g. `true`, when
  // an item was added without a reload); failing both, the id is looked up.
  // Never rejects. `keepalive` lets the request outlive the page.
  function sendRemoval(cached, passedId, rkey, keepalive) {
    if (!net) return Promise.resolve();
    function del(id) {
      if (!id || String(id).indexOf('local-') === 0 || id === true || id === 'true') return Promise.resolve();
      var init = { method: 'DELETE', credentials: 'same-origin' };
      if (keepalive) init.keepalive = true;
      return net(API + '/wishlists/mine/items/' + encodeURIComponent(id), init)
        .then(function (r) { return r.json(); }).catch(function () {});
    }
    if (cached && cached.id && String(cached.id).indexOf('local-') !== 0) return del(cached.id);
    if (passedId && passedId !== true && passedId !== 'true' && String(passedId).indexOf('local-') !== 0) return del(passedId);
    // Last resort: resolve the real id from the server, then delete.
    return netGetMine().then(function (server) {
      var x = server.items.filter(function (i) { return vkey(i.productId, i.variantId) === rkey; })[0];
      return del(x && x.id);
    }).catch(function () {});
  }

  /* ---------------- a removal that can be undone ----------------
   * The account Wishlist tab's Undo toast. The item leaves the local copy at
   * once, as with the optimistic form, but nothing goes to the Worker until
   * commit(): the toast calls it when it goes away, leaving the page sends it
   * (keepalive), and HOLD_MS is the backstop. Until then undo() puts the very
   * same item back in the local copy (its row id, saved date, alert switch and
   * bought mark) and answers true: nothing was sent, so nothing is saved again.
   * Once the removal has gone undo() answers false (the caller saves the item
   * again). Saving the item again meanwhile (a heart elsewhere on the page)
   * calls the removal off; removing it again sends it at once. `done` resolves
   * when the removal is settled either way, after its request; never rejects. */
  var HOLD_MS = 30 * 1000;
  var held = {}; // vkey -> the removal waiting on its Undo
  function removeWithUndo(payload) {
    payload = payload || {};
    var productId = payload.productId;
    var variantId = payload.variantId;
    var settle;
    var h = { done: new Promise(function (r) { settle = r; }) };
    if (productId == null || !loggedIn) {
      h.undo = function () { return false; };
      h.commit = function () { return h.done; };
      settle({ removed: false });
      return h;
    }
    var k = vkey(productId, variantId);
    if (held[k]) held[k].commit();
    var c = cacheGet(); var wl = (c && c.wl) ? c.wl : emptyWl();
    var at = -1;
    wl.items.forEach(function (i, n) { if (at < 0 && vkey(i.productId, i.variantId) === k) at = n; });
    var item = at >= 0 ? wl.items[at] : null;
    localRemove(wl, productId, variantId);
    cacheSet(wl); emitCount();
    var state = 'held';
    var timer = setTimeout(function () { h.commit(); }, HOLD_MS);
    h.undo = function () {
      if (state === 'undone') return true;
      if (state !== 'held') return false;
      state = 'undone'; clearTimeout(timer); delete held[k];
      var now = cacheGet(); var back = (now && now.wl) ? now.wl : emptyWl();
      if (!back.items.some(function (i) { return vkey(i.productId, i.variantId) === k; })) {
        back.items.splice(at < 0 ? back.items.length : Math.min(at, back.items.length), 0,
          item || { id: payload.wishlistItemId, productId: String(productId), variantId: variantId ? String(variantId) : null });
      }
      cacheSet(back); emitCount();
      settle({ removed: false });
      return true;
    };
    h.commit = function (keepalive) {
      if (state !== 'held') return h.done;
      state = 'sent'; clearTimeout(timer); delete held[k];
      fireKlaviyo('Removed from Wishlist', payload);
      rememberProps(productId, variantId, null);
      sendRemoval(item, payload.wishlistItemId, k, keepalive).then(function () { settle({ removed: true }); });
      return h.done;
    };
    held[k] = h;
    return h;
  }
  // Leaving the page (or hiding it, the last moment a phone reliably gives us)
  // sends every removal still waiting on its Undo.
  function sendHeld() { Object.keys(held).forEach(function (k) { if (held[k]) held[k].commit(true); }); }
  window.addEventListener('pagehide', sendHeld);
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') sendHeld(); });

  /* ---------------- guest -> account merge (once on login) ----------------
   * Guests could save to this device until 28 Oct 2026. Those saves still join
   * the account the first time the shopper signs in on that device. */
  function maybeMerge() {
    if (!loggedIn || !net) return;
    if (clubBlocked()) return; // Club members only: they join first; the device's old saves wait for them (kept below)
    var g = readStore(GUEST_KEY);
    if (!g || !g.items || !g.items.length) return;
    if (localStorage.getItem(MERGED_KEY)) return;
    net(API + '/wishlists/mine/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: g.items.map(function (i) {
          var it = { productId: String(i.productId), variantId: i.variantId != null ? String(i.variantId) : null };
          var p = cleanProps(i.properties);
          if (p) { it.properties = p; rememberProps(i.productId, i.variantId, p); }
          return it;
        }),
        ownerName: cfg.ownerName || undefined
      })
    }).then(function (r) {
      // The app refused: not a Club member. Keep the device's saves (not merged, not cleared) until they join.
      if (r.status === 403) return r.json().catch(function () { return null; }).then(function (e) { if (e && e.error === 'club_required') setClub({ required: true, member: false }); return null; });
      return r.json().then(function (res) {
        writeStore(MERGED_KEY, 1); delStore(GUEST_KEY);
        if (res && res.wishlist) { cacheSet(res.wishlist); emitCount(); }
      });
    }).catch(function () {});
  }

  /* ---------------- expose ---------------- */
  var WK = {
    config: {
      routes: { apiHost: API },
      storefront: { accessToken: cfg.storefrontToken || '', version: cfg.version || '2024-10' }
    },
    _apis: { wishlist: { headers: {} } },
    addWishlistItem: addWishlistItem,
    removeWishlistItem: removeWishlistItem,
    removeWithUndo: removeWithUndo,
    setItemAlert: setItemAlert,
    // Club members only (see the header). get(): { required, member } or null before we know; blocked(): signed in,
    // setting on, not a member; ready(cb): cb(state) once known; prompt(anchor): the shared join prompt; reveal(root).
    club: {
      get: function () { return clubNow; },
      blocked: clubBlocked,
      ready: ensureClub,
      prompt: promptJoin,
      reveal: reveal,
      joinUrl: joinHref
    }
  };
  window.WishlistKing = WK;
  window.KkWishlist = WK;

  // Hydrate the storefront token from a previous visit so product resolution
  // works on first paint even before any wishlist fetch completes.
  adoptStorefront(readStore(SFTOK_KEY));
  clubNow = readClub(); // what the app last told this device about Club-only saving

  // Paint the header counter from the cached count. Deferred scripts execute
  // while readyState is 'interactive' (before DOMContentLoaded fires), so wait
  // for DOMContentLoaded — by then every deferred script (incl. the navbar's
  // wk:countUpdated listener) has run. 'complete' = injected late; emit now.
  if (document.readyState === 'complete') emitCount();
  else {
    document.addEventListener('DOMContentLoaded', emitCount);
    window.addEventListener('load', emitCount); // covers the DCL-already-fired edge
  }

  maybeMerge();

  // Club members only: show the adverts this page carries (hidden until the app says the setting is on) and keep them in step.
  function revealAll() { reveal(document); refreshStaleClub(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', revealAll); else revealAll();
  document.addEventListener('wk:clubUpdated', revealAll);
})();
